import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Helmet } from 'react-helmet';

import { change, reset, getFormValues } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';
import OrderViewBar from 'components/workflow/OrderViewBar';
import ProductClaimView from 'components/workflow/ProductClaimView';
import OrderViewInformation from 'components/workflow/OrderViewInformation';
import OrderDetailsToolbar from 'components/workflow/OrderDetailsToolbar';
import OrdersActionsBar from 'components/workflow/OrdersActionsBar';
import PrintDialog from 'components/common/PrintDialog';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';
import StickyToolbar from 'components/common/StickyToolbar';

import { clearOrders, fetchOrder } from 'redux/modules/orders';
import { fetchArchiveOrder } from 'redux/modules/archiveOrders';
import { clearConnectedOrders } from 'redux/modules/connectedOrders';
import { fetchBusiness } from 'redux/modules/business';
import { fetchSystemSettings } from 'redux/modules/systemSettings';

import formatOrderNumber from 'utils/formatOrderNumber';

import { appTitle, titleSeparator } from 'config/helmetSettings';
import orderStatusesAutoChange from 'config/orderStatusesAutoChange';

const styles = {
  grid: {
    marginTop: -27,
  },
  gutterBottom: {
    height: 72,
  },
};

class OrderView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      printDialogOpen: false,
      signDialogOpen: false,
      printFormValues: null,
    };

    this.onSelectedOrderLinesPrint = this.onSelectedOrderLinesPrint.bind(this);
    this.onPrintButtonClick = this.onPrintButtonClick.bind(this);
    this.onPrintDialogClose = this.onPrintDialogClose.bind(this);
    this.onPrintDialogSign = this.onPrintDialogSign.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onProductClaimViewSubmitSuccess = this.onProductClaimViewSubmitSuccess.bind(this);
  }

  componentWillMount() {
    this.props.actions.clearOrders();
    this.props.actions.clearConnectedOrders();
  }

  componentDidMount() {
    const { match, actions, archive } = this.props;
    if (archive) {
      actions.fetchArchiveOrder({
        ID: match.params.id,
        triggerLoading: true,
      });
    } else {
      actions.fetchOrder({
        ID: match.params.id,
        triggerLoading: true,
      });
    }
    actions.fetchBusiness({
      triggerLoading: false,
    });
    actions.fetchSystemSettings({
      triggerLoading: false,
    });
  }

  componentWillReceiveProps(nextProps) {
    const { history, loading, connectedOrdersOrderLineID } = this.props;
    if (loading > 0 && nextProps.loading === 0 && !nextProps.order) {
      history.replace('/workflow');
    }
    if (nextProps.connectedOrdersOrderLineID.length > 0
      && JSON.stringify(nextProps.connectedOrdersOrderLineID)
      !== JSON.stringify(connectedOrdersOrderLineID)) {
      this.props.actions.clearConnectedOrders();
    }
  }

  onSelectedOrderLinesPrint() {
    this.setState({ printDialogOpen: true });
  }

  onPrintButtonClick() {
    this.setState({ printDialogOpen: true });
  }

  onPrintDialogClose() {
    this.setState({ printDialogOpen: false });
  }

  onPrintDialogSign(values) {
    this.setState({
      printDialogOpen: false,
      printFormValues: values,
      signDialogOpen: true,
    });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
  }

  onProductClaimViewSubmitSuccess(values) {
    const { match, actions } = this.props;
    const orderLinesWithStatusesAutoChange = values.filter(item =>
      orderStatusesAutoChange[item.Status]);

    if (orderLinesWithStatusesAutoChange.length > 0) {
      actions.fetchOrder({
        ID: match.params.id,
        triggerLoading: false,
      });
    }
  }

  render() {
    const {
      order,
      loading,
      orderLines,
      selectedOrderLines,
      business,
      systemSettings,
      actions,
      history,
      location,
      record,
      archive,
      classes,
    } = this.props;
    const {
      printDialogOpen,
      signDialogOpen,
      printFormValues,
    } = this.state;
    const ordersActionsBarActive = selectedOrderLines.length > 0;
    return (
      <Layout>
        {(loading > 0 || !order) ? (
          <Progress />
        ) : (
          <Fragment>
            <Helmet>
              <title>
                {appTitle} {titleSeparator} View {formatOrderNumber(order.ID)}
              </title>
            </Helmet>
            <StickyToolbar disableGutters>
              <OrderViewBar
                {...order}
                archive={archive}
                business={business}
              />
            </StickyToolbar>
            <OrderDetailsToolbar
              variant="view"
              onSubmitButtonClick={this.onPrintButtonClick}
              onCancelButtonClick={() => {
                if (!record || record[0].pathname === location.pathname) {
                  window.close();
                } else {
                  history.goBack();
                }
              }}
            />
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item xs={8}>
                <ProductClaimView
                  form="orderViewForm"
                  syncUpdates={selectedOrderLines.length > 0}
                  business={business}
                  systemSettings={systemSettings}
                  onSubmitSuccess={this.onProductClaimViewSubmitSuccess}
                  disabled={archive}
                  {...order}
                />
              </Grid>
              <Grid item xs={4}>
                <OrderViewInformation {...order} />
              </Grid>
            </Grid>
            <Collapse
              in={ordersActionsBarActive}
              direction="up"
              mountOnEnter
              unmountOnExit
            >
              <div className={classes.gutterBottom} role="presentation" />
            </Collapse>
            <OrdersActionsBar
              open={ordersActionsBarActive}
              count={selectedOrderLines.length}
              totalCount={orderLines.length}
              selectedOrderLines={selectedOrderLines}
              onSelect={() => {
                orderLines.forEach((item) => {
                  actions.change('orderViewForm', `${item.ID}-orderLine.selected`, true);
                });
              }}
              onDeselect={() => {
                orderLines.forEach((item) => {
                  actions.change('orderViewForm', `${item.ID}-orderLine.selected`, false);
                });
              }}
              variant="view"
              onSubmitSuccess={() => {
                actions.reset('orderViewForm');
              }}
              onPrint={this.onSelectedOrderLinesPrint}
              archive={archive}
            />
            <PrintDialog
              form={`${order.ID}-orderPrint`}
              variant="order"
              open={printDialogOpen}
              onClose={this.onPrintDialogClose}
              onSign={this.onPrintDialogSign}
              ID={order.ID}
              patient={order.Patient}
              diagnosis={order.Diagnosis}
              prescriber={order.Prescriber}
              facility={order.Facility}
              business={business}
              systemSettings={systemSettings}
              {...{
                'Order Line': selectedOrderLines.length > 0
                  ? selectedOrderLines : order['Order Line'],
                'Primary Insurance': order['Primary Insurance'],
                'Order Insurance Param': order['Order Insurance Param'],
              }}
            />
            <DocumentsSignDialog
              open={signDialogOpen}
              onClose={this.onSignDialogClose}
              printFormValues={printFormValues}
              variant="order"
              ID={order.ID}
              patient={order.Patient}
              prescriber={order.Prescriber}
              {...{
                'Order Line': selectedOrderLines.length > 0
                  ? selectedOrderLines : order['Order Line'],
                'Primary Insurance': order['Primary Insurance'],
                'Order Insurance Param': order['Order Insurance Param'],
              }}
            />
          </Fragment>
        )}
      </Layout>
    );
  }
}

OrderView.propTypes = {
  order: PropTypes.shape({
    ID: PropTypes.number,
  }),
  loading: PropTypes.number.isRequired,
  orderLines: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  selectedOrderLines: PropTypes.arrayOf(PropTypes.shape({
    ID: PropTypes.number,
  })).isRequired,
  connectedOrdersOrderLineID: PropTypes.arrayOf(PropTypes.number),
  actions: PropTypes.shape({
    clearOrders: PropTypes.func.isRequired,
    fetchOrder: PropTypes.func.isRequired,
    clearConnectedOrders: PropTypes.func.isRequired,
    change: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
    fetchSystemSettings: PropTypes.func.isRequired,
  }).isRequired,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  systemSettings: PropTypes.object,
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  record: PropTypes.arrayOf(PropTypes.shape({
    pathname: PropTypes.string,
  })).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

OrderView.defaultProps = {
  order: null,
  connectedOrdersOrderLineID: [],
  business: null,
  systemSettings: null,
};

const mapStateToProps = (store, ownProps) => {
  const orderViewFormValues = getFormValues('orderViewForm')(store);
  let order;
  if (ownProps.archive) {
    order = store.archiveOrders.data.length > 0 && !store.form.ordersFilter
      ? store.archiveOrders.data.find(order => order.ID === Number(ownProps.match.params.id))
      : null;
  } else {
    order = store.orders.data.length > 0 && !store.form.ordersFilter
      ? store.orders.data.find(order => order.ID === Number(ownProps.match.params.id))
      : null;
  }
  // !store.form.ordersFilter - check if order lines are not filtered by State
  const orderLines = order ? order['Order Line'] : [];
  return {
    order,
    loading: store.orders.loading
      + store.archiveOrders.loading,
    orderLines,
    selectedOrderLines: orderViewFormValues
      ? Object.keys(orderViewFormValues)
        .filter(key => orderViewFormValues[key].selected === true)
        .map(key => orderLines.find(item => item.ID === Number(key.replace('-orderLine', ''))))
      : [],
    connectedOrdersOrderLineID: store.connectedOrders.orderLineID,
    business: store.business.data,
    systemSettings: store.systemSettings.data,
    record: store.routerParams.record,
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    clearOrders,
    fetchOrder,
    fetchArchiveOrder,
    clearConnectedOrders,
    change,
    reset,
    fetchBusiness,
    fetchSystemSettings,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(OrderView),
);



// WEBPACK FOOTER //
// ./src/pages/workflow/OrderView.js